import { useOrganizationBusinessUnits } from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  PageWithActions,
} from '@airship-platform/jam-web/components';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import { Link, useParams } from 'react-router';
import { useAppointmentEquipment } from '../../../../data-access/appointmentEquipment';
import { useAppointments } from '../../../../data-access/appointments';
import { useServiceHistory } from '../../../../data-access/serviceHistory';
import { CustomerContactsButton } from '../../../../patterns';
import ActivitiesSummary from './ActivitiesSummary';
import Address from './Address';
import CustomerInfo from './CustomerInfo';
import ExistingSystem from './ExistingSystem';
import Overview from './Overview';
import ServiceCallAction from './ServiceCallAction';
import ServiceHistory from './ServiceHistory';

const ScheduleDetailsPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { currentAppointment, isLoading } = useAppointments();
  const { businessUnits } = useOrganizationBusinessUnits();

  const {
    serviceHistory,
    isLoading: isLoadingHistory,
    isLoaded: isLoadedHistory,
  } = useServiceHistory();

  const {
    equipment,
    isLoading: isLoadingEquipment,
    isLoaded: isLoadedEquipment,
  } = useAppointmentEquipment();

  const { customer, appointment, job, location, activities } =
    currentAppointment || {};
  if (!(appointmentId && customer && job && appointment && location)) {
    return (
      <EmptyState
        title="Appointment not found"
        subtitle="Oops! The requested appointment was not found."
      />
    );
  }

  const mappedJob = {
    startDateTime: appointment.startDateTime,
    name: customer.name,
    address1: location.address1,
    city: location.city,
    state: location.state,
    priority: job.priority,
    tagTypes: job.tagTypes,
    isNewCustomer: isLoadedHistory && !serviceHistory.length,
    businessUnitName: businessUnits.find(
      (unit) => unit.organizationBusinessUnitId === job.businessUnitId
    )?.name,
  };

  return (
    <PageWithActions
      actionsComponent={
        <FlexBox
          alignItems={'center'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <Button component={Link} color={'softGold'} to={'..'}>
            Back
          </Button>
          <ServiceCallAction appointmentId={appointmentId} />
        </FlexBox>
      }
    >
      <FlexBox flexDirection={'column'} gap={4}>
        <FlexBox flexDirection={['column', 'row']} gap={[4, 3]}>
          <FlexBox flexDirection={'column'} alignItems={'flex-start'} flex={1}>
            <CustomerInfo {...mappedJob} />
          </FlexBox>
          <FlexBox
            flexDirection={'column'}
            width={['100%', 'fit-content']}
            p={[2, 0]}
            gap={2}
          >
            <Card variant={'bordered'}>
              <CustomerContactsButton fullWidth customerName={customer.name} />
            </Card>
            <ActivitiesSummary activities={activities || []} />
          </FlexBox>
        </FlexBox>
        <Divider />
        <Overview job={job} />
        <Divider />
        <Address location={location} />
        <Divider />
        <ExistingSystem
          isLoading={isLoading || (isLoadingEquipment && !isLoadedEquipment)}
          equipment={equipment}
        />
        <Divider />
        <ServiceHistory
          isLoading={isLoading || (isLoadingHistory && !isLoadedHistory)}
          serviceHistory={serviceHistory}
        />
      </FlexBox>
    </PageWithActions>
  );
};

export default ScheduleDetailsPage;
